import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { parse, stringify } from 'qs'
import { MaterialReactTable } from 'material-react-table'
import { MRT_Localization_ES } from 'material-react-table/locales/es'

import { useCombos } from '../../../combos'
import { useJCAContext } from '../../contexts'
import { jcaTableDataPreparation } from '../../logics'
import { JCA_TABLE_COLUMNS, DEFAULT_QUERY } from './jca-table.constants'
import { jcaTableStyles } from './jca-table.styles'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const JCATable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useCombos([])
  const {
    data,
    pagination: { total_registros: totalEntries },
    loading,
    search,
    onClickTableRow,
    getTableData,
  } = useJCAContext()
  const permissions = useSelector((state) => state.global.permissions)

  const [pagination, setPagination] = useState({
    pageIndex: search.page ? search.page - 1 : 0,
    pageSize: search.size || 10,
  })

  useEffect(() => {
    const params = parse(location.search, { ignoreQueryPrefix: true })
    const pageIndex = params.page ? Number(params.page) - 1 : 0
    const pageSize = params.size ? Number(params.size) : 10

    setPagination({ pageIndex, pageSize })

    if (location.search.substring(1)) {
      getTableData(params)
    } else {
      navigate(`/jca?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getTableData, navigate])

  const handlePaginationChange = (updater) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater
    const currentParams = parse(location.search, { ignoreQueryPrefix: true })
    const newSearch = {
      ...currentParams,
      page: newPagination.pageIndex + 1,
      size: newPagination.pageSize,
    }
    navigate(`?${stringify(newSearch)}`)
  }

  const formattedColumns = useMemo(
    () =>
      JCA_TABLE_COLUMNS.map((column) => {
        if (column.field === 'actions') {
          return {
            header: formatMessage({ id: `pages.jca.table.columns.${column.field}.title` }),
            accessorKey: column.field,
          }
        }
        return {
          header: formatMessage({ id: `pages.jca.table.columns.${column.field}.title` }),
          accessorKey: column.field,
          muiTableBodyCellProps: {
            style: {
              ...jcaTableStyles.cell,
              ...column.cellStyle,
              minWidth: column.width,
            },
          },
          Cell: ({ cell }) => {
            let value = cell.getValue()
            
            // Mapear valores de combo si existe comboId
            if (column.comboId && combos[column.comboId]?.data) {
              const lookup = getLookup(combos[column.comboId].data)
              value = lookup[value] ?? value
            }
            
            return value || <span>--</span>
          },
        }
      }),
    [combos, formatMessage]
  )

  const preparedData = useMemo(
    () => jcaTableDataPreparation({ data, combos, intl }),
    [data, combos, intl]
  )

  const handleRowClick = useCallback(
    (_, row) => {
      onClickTableRow(row['id'], search)
    },
    [onClickTableRow, search]
  )

  const customLocalization = useMemo(
    () => ({
      ...MRT_Localization_ES,
      rowsPerPage: 'Registros',
    }),
    []
  )

  return (
    <MaterialReactTable
      columns={formattedColumns}
      data={preparedData || []}
      enableTopToolbar={false}
      enableRowSelection={false}
      enableColumnDragging={false}
      muiTablePaperProps={{ elevation: 0 }}
      muiTableProps={{ sx: { tableLayout: 'fixed' } }}
      muiTableHeadCellProps={{
        style: jcaTableStyles.header,
      }}
      muiTableBodyRowProps={
        permissions.ver_detalle_jca
          ? ({ row }) => ({
              onClick: () => handleRowClick(null, row.original),
              sx: {
                cursor: 'pointer',
              },
            })
          : undefined
      }
      state={{ isLoading: loading, pagination }}
      manualPagination
      rowCount={totalEntries || 0}
      onPaginationChange={handlePaginationChange}
      localization={customLocalization}
    />
  )
}
